import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck, ArrowRight, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_LINK = "https://api.whatsapp.com/send?phone=34630666734&text=Quiero%20mi%20cup%C3%B3n%20descuento%20de%20100%E2%82%AC";

const SIZES = [
  "90x190", "90x200", "105x190", "135x190", "135x200",
  "140x200", "150x190", "150x200", "160x200", "180x200", "200x200",
  "Otra / Medida especial",
];

export default function LeadForm({ variant = "light", compact = false, idPrefix = "lead", source = "cheque" }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    size_pref: "",
    consent: false,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Debes aceptar la política de privacidad para continuar.");
      return;
    }
    if (!/^[+\d\s().-]{6,}$/.test(form.phone)) {
      toast.error("Introduce un teléfono válido para que el asesor pueda agendar tu cita.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/leads`, { ...form, source });
      // GTM conversion event
      if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: "lead_submit",
          lead_source: source,
          lead_form_id: idPrefix,
          size_pref: form.size_pref || "",
          lead_id: res.data?.id,
        });
      }
      // Meta Pixel — Lead event (browser) — shares event_id with server CAPI for dedup
      if (typeof window !== "undefined" && typeof window.fbq === "function" && res.data?.id) {
        window.fbq("track", "Lead", {
          content_name: "Cheque 100€ Core Colchón",
          content_category: "cheque",
          currency: "EUR",
          value: 100,
        }, { eventID: res.data.id });
      }
      setDone(true);
      // Redirect to /gracias with lead id so user can download the voucher
      navigate("/gracias", {
        state: { leadId: res.data?.id, leadName: res.data?.name },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No hemos podido enviar tu solicitud. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const isDark = variant === "dark";
  const labelCls = isDark ? "text-white/70" : "text-[#5C5366]";
  const inputCls = `w-full bg-transparent border-b-2 ${
    isDark ? "border-white/30 text-white placeholder-white/40 focus:border-white" : "border-[#E3DDE8] text-[#2E2638] placeholder-[#9A93A2] focus:border-[#8B1538]"
  } px-0 py-3 text-base outline-none transition-colors`;

  if (done) {
    return (
      <div
        data-testid={`${idPrefix}-success`}
        className={`p-8 ${isDark ? "bg-white/10 border-white/20" : "bg-[#F9F8FA] border-[#E3DDE8]"} border text-center`}
      >
        <div className={`mx-auto mb-4 w-14 h-14 flex items-center justify-center ${isDark ? "bg-white/15" : "bg-[#8B1538]/10"} rounded-full`}>
          <ShieldCheck className={isDark ? "text-white" : "text-[#8B1538]"} size={28} />
        </div>
        <h3 className={`font-display text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-[#2E2638]"}`}>
          ¡Tu cheque está en camino!
        </h3>
        <p className={isDark ? "text-white/80" : "text-[#5C5366]"}>
          Recibirás un email con tu <strong>cheque de 100€</strong> en los próximos minutos.
          Un asesor de Core Colchón te llamará para <strong>agendar tu cita en la tienda de Navalcarnero</strong>.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid={`${idPrefix}-form`}
      className={`${compact ? "" : "p-8"} ${
        isDark ? "" : "bg-white"
      } ${compact ? "" : isDark ? "border border-white/15" : "border border-[#E3DDE8] shadow-[0_8px_30px_rgba(46,38,56,0.06)]"}`}
    >
      {!compact && (
        <>
          <div className={`text-xs uppercase tracking-[0.2em] font-bold ${isDark ? "text-white/80" : "text-[#8B1538]"} mb-2`}>
            Solicita tu cheque · Sin compromiso
          </div>
          <h3 className={`font-display text-3xl sm:text-4xl font-bold mb-2 ${isDark ? "text-white" : "text-[#2E2638]"}`}>
            Tu cheque de 100€
          </h3>
          <p className={`mb-6 ${isDark ? "text-white/70" : "text-[#5C5366]"}`}>
            Te lo enviamos al email. Lo presentas en tienda, te asesoramos y te ahorras 100€ en tu colchón Core.
          </p>
        </>
      )}

      <div className="space-y-5">
        <div>
          <label className={`text-xs uppercase tracking-wider ${labelCls}`} htmlFor={`${idPrefix}-name`}>Nombre</label>
          <input
            id={`${idPrefix}-name`}
            data-testid={`${idPrefix}-input-name`}
            type="text"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Tu nombre"
            className={inputCls}
          />
        </div>
        <div>
          <label className={`text-xs uppercase tracking-wider ${labelCls}`} htmlFor={`${idPrefix}-email`}>Email</label>
          <input
            id={`${idPrefix}-email`}
            data-testid={`${idPrefix}-input-email`}
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="tu@email.com"
            className={inputCls}
          />
        </div>
        <div>
          <label className={`text-xs uppercase tracking-wider ${labelCls}`} htmlFor={`${idPrefix}-phone`}>
            Teléfono <span className="lowercase font-normal">— para que el asesor agende tu cita</span>
          </label>
          <input
            id={`${idPrefix}-phone`}
            data-testid={`${idPrefix}-input-phone`}
            type="tel"
            required
            value={form.phone}
            onChange={set("phone")}
            placeholder="600 000 000"
            className={inputCls}
          />
          <p className={`text-xs mt-2 ${isDark ? "text-white/50" : "text-[#5C5366]"}`}>
            Te llamamos para <strong>reservarte hora</strong> y que no tengas que esperar en tienda.
          </p>
        </div>
        <div>
          <label className={`text-xs uppercase tracking-wider ${labelCls}`} htmlFor={`${idPrefix}-size`}>Medida que te interesa <span className="lowercase font-normal">(opcional)</span></label>
          <select
            id={`${idPrefix}-size`}
            data-testid={`${idPrefix}-input-size`}
            value={form.size_pref}
            onChange={set("size_pref")}
            className={inputCls}
          >
            <option value="">Selecciona una medida…</option>
            {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <label className={`flex items-start gap-3 text-xs ${isDark ? "text-white/70" : "text-[#5C5366]"}`}>
          <input
            type="checkbox"
            data-testid={`${idPrefix}-consent`}
            checked={form.consent}
            onChange={set("consent")}
            className="mt-0.5 accent-[#8B1538]"
          />
          <span>
            Acepto recibir información comercial de Core Colchón y la <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="underline">política de privacidad</a> (RGPD).
          </span>
        </label>

        <button
          type="submit"
          data-testid={`${idPrefix}-submit`}
          disabled={loading}
          className="group w-full mt-2 px-6 py-4 bg-[#8B1538] hover:bg-[#6A0F2A] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm transition-all duration-300 shadow-[0_4px_14px_rgba(139,21,56,0.39)] hover:shadow-[0_8px_24px_rgba(139,21,56,0.45)] hover:-translate-y-0.5 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : null}
          Quiero mi cheque de 100€
          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
        </button>

        <div className={`flex items-center gap-3 my-1 text-[10px] uppercase tracking-[0.2em] font-bold ${isDark ? "text-white/40" : "text-[#B5AFBE]"}`}>
          <span className={`flex-1 h-px ${isDark ? "bg-white/15" : "bg-[#E3DDE8]"}`}/>
          <span>o solicítalo por</span>
          <span className={`flex-1 h-px ${isDark ? "bg-white/15" : "bg-[#E3DDE8]"}`}/>
        </div>

        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${idPrefix}-whatsapp`}
          className="group w-full px-6 py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase tracking-wider text-sm transition-all duration-300 shadow-[0_4px_14px_rgba(5,150,105,0.35)] hover:shadow-[0_8px_24px_rgba(5,150,105,0.45)] hover:-translate-y-0.5 flex items-center justify-center gap-2"
        >
          <MessageCircle size={18}/>
          Pedirlo por WhatsApp
        </a>

        <p className={`text-[11px] text-center ${isDark ? "text-white/50" : "text-[#5C5366]"}`}>
          Cheque canjeable únicamente en la tienda física de Navalcarnero (Madrid).
        </p>
      </div>
    </form>
  );
}
